import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Container, Grid, Typography, Paper, TextField, Button, List, ListItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import { CheckCircle, DirectionsBus, MonetizationOn, People, Security } from '@mui/icons-material';
import axios from 'axios';
import useTranslation from '../hooks/useTranslation';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import tokenManager from '../services/tokenManager';

const OperatorPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    description: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    if (token) {
      // Check if token actually needs refreshing
      if (!tokenManager.shouldRefreshToken()) {
        // Token is still valid, use cached user data
        const userData = localStorage.getItem('userData');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            if (user.role === 'operator') {
              history.replace('/operator/dashboard');
              return;
            } else if (user.role === 'admin') {
              history.replace('/admin');
              return;
            }
          } catch (error) {
            console.error('Error parsing cached user data:', error);
          }
        }
      }

      // Token needs refresh or no cached data - verify token and redirect based on role
      axios.post('/auth/refresh-token')
        .then(response => {
          const { role } = response.data.user;
          localStorage.setItem('userData', JSON.stringify(response.data.user));
          if (role === 'operator') {
            history.replace('/operator/dashboard');
          } else if (role === 'admin') {
            history.replace('/admin');
          }
        })
        .catch(error => {
          console.error('Token verification failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('userData');
        });
    }
  }, [history]);

  const benefits = [
    {
      icon: <People sx={{ fontSize: 48 }} />,
      title: t('pages.tours.operator.landingPage.benefits.customers.title'),
      description: t('pages.tours.operator.landingPage.benefits.customers.description')
    },
    {
      icon: <MonetizationOn sx={{ fontSize: 48 }} />,
      title: t('pages.tours.operator.landingPage.benefits.revenue.title'),
      description: t('pages.tours.operator.landingPage.benefits.revenue.description')
    },
    {
      icon: <DirectionsBus sx={{ fontSize: 48 }} />,
      title: t('pages.tours.operator.landingPage.benefits.management.title'),
      description: t('pages.tours.operator.landingPage.benefits.management.description')
    },
    {
      icon: <Security sx={{ fontSize: 48 }} />,
      title: t('pages.tours.operator.landingPage.benefits.payments.title'),
      description: t('pages.tours.operator.landingPage.benefits.payments.description')
    }
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = t('pages.tours.operator.landingPage.form.validation.nameRequired');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('pages.tours.operator.landingPage.form.validation.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('pages.tours.operator.landingPage.form.validation.emailInvalid');
    }
    if (formData.phone && !/^\+?[\d\s-]+$/.test(formData.phone)) {
      newErrors.phone = t('pages.tours.operator.landingPage.form.validation.phoneInvalid');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      await axios.post('/operators/request', formData);
      toast.success(t('pages.tours.operator.landingPage.form.success'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        description: ''
      });
      setErrors({});
    } catch (error) {
      console.error('Error submitting request:', error);
      if (error.response?.data?.name === 'SequelizeUniqueConstraintError' || 
          (error.response?.data?.errors && error.response?.data?.errors[0]?.type === 'unique violation') ||
          error.response?.data?.error?.includes('duplicate key value violates unique constraint')) {
        setErrors({ email: t('pages.tours.operator.landingPage.form.emailExists') });
        toast.error(t('pages.tours.operator.landingPage.form.emailExists'));
      } else {
        toast.error(t('pages.tours.operator.landingPage.form.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  return (
    <Box sx={{ bgcolor: '#f5f7f9', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar hideFilters />
      <Box sx={{ py: { xs: 4, md: 8 }, flex: 1 }}>
        <Container maxWidth="lg">
          {/* Hero Section */}
          <Grid container spacing={4} sx={{ mb: { xs: 4, md: 8 } }}>
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
                <Typography 
                  variant="h2" 
                  component="h1" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 700,
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 2
                  }}
                >
                  {t('pages.tours.operator.landingPage.title')}
                </Typography>
                <Typography 
                  variant="h5" 
                  color="text.secondary" 
                  paragraph
                  sx={{ fontSize: { xs: '1.1rem', md: '1.3rem' } }}
                >
                  {t('pages.tours.operator.landingPage.subtitle')}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Benefits Section */}
          <Grid container spacing={4} sx={{ mb: { xs: 4, md: 8 } }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 4, 
                    height: '100%', 
                    textAlign: 'center', 
                    bgcolor: 'white',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 3 }}>
                    {benefit.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Features and Get Started Section */}
          <Grid container spacing={4} sx={{ mb: { xs: 4, md: 8 } }}>
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  bgcolor: 'white',
                  height: '100%',
                  borderRadius: 2
                }}
              >
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('pages.tours.operator.landingPage.whyUs.title')}
                </Typography>
                <List>
                  {t('pages.tours.operator.landingPage.whyUs.features', { returnObjects: true }).map((feature, index) => (
                    <ListItem key={index} sx={{ py: 1 }}>
                      <ListItemIcon>
                        <CheckCircle color="primary" />
                      </ListItemIcon>
                      <ListItemText 
                        primary={feature} 
                        primaryTypographyProps={{ 
                          sx: { fontWeight: 500 }
                        }} 
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>

            {/* Get Started Form */}
            <Grid item xs={12} md={6}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 4, 
                  bgcolor: 'white',
                  borderRadius: 2
                }}
              >
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  {t('pages.tours.operator.landingPage.form.title')}
                </Typography>
                <Typography variant="body1" paragraph color="text.secondary">
                  {t('pages.tours.operator.landingPage.form.subtitle')}
                </Typography>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label={t('pages.tours.operator.landingPage.form.businessName')}
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        error={!!errors.name}
                        helperText={errors.name}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label={t('pages.tours.operator.landingPage.form.email')}
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        error={!!errors.email}
                        helperText={errors.email}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t('pages.tours.operator.landingPage.form.phone')}
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label={t('pages.tours.operator.landingPage.form.description')}
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={loading}
                        sx={{
                          py: 1.5,
                          textTransform: 'none',
                          fontSize: '1.1rem',
                          fontWeight: 600
                        }}
                      >
                        {loading ? (
                          <CircularProgress size={24} color="inherit" />
                        ) : (
                          t('pages.tours.operator.landingPage.form.submit')
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
};

export default OperatorPage; 